import {
  Box,
  Button,
  Flex,
  Icon,
  InputAdornment,
  InputControl,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { UserIcon } from '@tonic-one/react-icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidProps } from '@Types/index';
import { validateEmail, validateIsNotEmptyString } from '@Utils/validate';
import { getValidateMsg } from '@Utils/common';
import { InlineError } from '@Components/Common/FormikComponent';
import { AccountApi } from '@Apis';
import { useNavigate } from 'react-router-dom';
import { ResetPasswordStep } from '../index';
import ReCAPTCHA from 'react-google-recaptcha';
import config from 'src/config';

export const Forgot = ({
  setResetPasswordStep,
}: {
  setResetPasswordStep: (value: ResetPasswordStep) => void;
}) => {
  const [colorMode] = useColorMode();
  const [forgotErrorMsg, setForgotErrorMsg] = useState<string | undefined>();
  const recaptchaRef = React.createRef<ReCAPTCHA>();
  const [colorStyle] = useColorStyle({ colorMode });
  const [username, setUsername] = useState<string>('');
  const iconColor = colorStyle.color.tertiary;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = React.useState<string | undefined>(undefined);
  const validateOrgName: ValidProps[] = [
    {
      validMethod: validateIsNotEmptyString,
      errMsg: t('login.username.validate.isEmpty'),
    },
    {
      validMethod: validateEmail,
      errMsg: t('common.error.invalidEmailAddress'),
    },
  ];

  const validateUsername = (value: string) => {
    const msg = getValidateMsg(validateOrgName, value);
    console.log('validateUsername', msg);
    setErrorMsg(msg);
    return msg;
  };

  const restPassword = async () => {
    console.log(recaptchaRef?.current?.getValue());
    console.log('restPassword');
    const msg = validateUsername(username);
    if (msg !== undefined) {
      return;
    }
    try {
      const token = recaptchaRef?.current?.getValue();
      if (!token) {
        setForgotErrorMsg(t('login.recaptcha.error'));
        return;
      }
      const res = await AccountApi.forgotPassword(username, token);
      if (res.data.status !== 0) {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
        return;
      }
      setUsername('');
      window.showNotification({
        titleMsg: t('login.resetPassword.sent.success'),
        severity: 'success',
      });
    } catch (error: any) {
      console.error(error);
      if (
        error.status === 400 &&
        error.data.error.details[0].code === 'invalid_token'
      ) {
        setForgotErrorMsg(t('login.recaptcha.error'));
        return;
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } finally {
      recaptchaRef.current?.reset();
    }
  };
  return (
    <Box width={493} p="40px 40px">
      <Flex alignItems="stretch" flexDirection="row">
        <Box flex="auto" color={colorStyle.color.primary}>
          <Text fontSize="lg" lineHeight="lg" mb={20}>
            {t('login.text.resetPassword')}
          </Text>
          {forgotErrorMsg && (
            <Box mb={8}>
              <InlineError>{forgotErrorMsg}</InlineError>
            </Box>
          )}
          <Box>
            <InputControl
              placeholder={t('login.username.placeholder')}
              width={413}
              value={username}
              onChange={(e: any) => {
                setUsername(e.target.value);
              }}
              startAdornment={
                <InputAdornment>
                  <Icon as={UserIcon} color={iconColor} />
                </InputAdornment>
              }
              error={errorMsg !== undefined}
            />
            <Box>
              {errorMsg !== undefined && <InlineError>{errorMsg}</InlineError>}
            </Box>
          </Box>
          <Box m="24px 0px" height="76px">
            <ReCAPTCHA
              theme={colorMode === 'dark' ? 'dark' : 'light'}
              ref={recaptchaRef}
              onChange={() => {
                setForgotErrorMsg(undefined);
              }}
              sitekey={config.ui.reCaptcha.sitekey}
            />
          </Box>
          <Text>
            <Flex alignItems="stretch" justifyContent="flex-start" mt={22}>
              <Button
                variant="primary"
                onClick={restPassword}
                width={201}
                height={40}
              >
                {t('login.text.resetPassword')}
              </Button>
              <Button
                variant="secondary"
                ml={8}
                width={201}
                height={40}
                onClick={() => {
                  navigate('/login');
                  setResetPasswordStep(ResetPasswordStep.None);
                }}
              >
                {t('login.text.backToLogin')}
              </Button>
            </Flex>
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};
